import { inject } from '@angular/core';
import { Router, type Routes } from '@angular/router';

import {
  alumbradoGuard,
  sesionGuard,
  vistaGuard,
  type PantallaDeRuta,
} from '@core/permisos/guards';

/**
 * Rutas de FARO.
 *
 * **Los crear/editar no tienen ruta**: son diálogos que se abren desde el
 * listado o desde el detalle. Por eso no hay `luminarias/nueva` ni
 * `luminarias/:id/editar`.
 *
 * Forma de la URL (`30-producto/01-mapa-de-navegacion.md` §4):
 *  - sin prefijo `/main`: en FARO todo está adentro;
 *  - sustantivos en minúscula, y la ruta del detalle es la del recurso
 *    (`/inventario/luminarias/:id`, no `/luminarias/detalles/:id`);
 *  - filtros y solapas en query params, para que un listado filtrado sea un
 *    link que se comparte.
 */

/**
 * Azúcar para declarar qué pantalla es una ruta: se entra con lo que pide
 * `VISTAS` (`core/permisos/vistas.ts`), lo mismo que usa el menú.
 * Con `data` propio, se agrega `pantalla` a mano junto a `canActivate: [vistaGuard]`.
 */
const ve = (pantalla: PantallaDeRuta) => ({
  canActivate: [vistaGuard],
  data: { pantalla },
});

/** Grupos de luminarias y de puestas comparten ruta: decide la categoría. */
const pantallaGrupos: PantallaDeRuta = (ruta) =>
  /^puestas?$/i.test(ruta.paramMap.get('categoria') ?? '') ? 'gruposPuestas' : 'gruposLuminarias';

export const routes: Routes = [
  // El correo de recuperación que arma el backend apunta a
  // `${FRONTEND_URL}/login/reset?token=…`, la ruta de IRIX. Se aceptan las dos
  // rutas de IRIX y caen en las propias; una redirección con texto conserva los
  // query params (el token).
  { path: 'login/reset', redirectTo: '/restablecer-clave', pathMatch: 'full' },
  { path: 'login/forgot-password', redirectTo: '/olvide-mi-clave', pathMatch: 'full' },
  {
    path: 'olvide-mi-clave',
    loadComponent: () =>
      import('@core/sesion/olvide-clave/olvide-clave.component').then(
        (m) => m.OlvideClaveComponent,
      ),
    title: 'Olvidé mi contraseña · FARO',
  },
  {
    path: 'restablecer-clave',
    loadComponent: () =>
      import('@core/sesion/restablecer-clave/restablecer-clave.component').then(
        (m) => m.RestablecerClaveComponent,
      ),
    title: 'Contraseña nueva · FARO',
  },
  {
    path: 'login',
    loadComponent: () => import('@core/sesion/login/login.component').then((m) => m.LoginComponent),
    title: 'Entrar · FARO',
  },
  {
    path: 'sin-acceso',
    canActivate: [sesionGuard],
    loadComponent: () =>
      import('@core/sesion/sin-acceso/sin-acceso.component').then((m) => m.SinAccesoComponent),
    title: 'Sin acceso · FARO',
  },

  {
    path: '',
    canActivate: [sesionGuard, alumbradoGuard],
    loadComponent: () => import('@ui/layout/shell.component').then((m) => m.ShellComponent),
    children: [
      { path: '', redirectTo: 'panorama', pathMatch: 'full' },

      // ── Guardia ────────────────────────────────────────────────────────────
      {
        path: 'panorama',
        loadComponent: () =>
          import('@dominio/panorama/panorama.component').then((m) => m.PanoramaComponent),
        title: 'Panorama · FARO',
      },
      {
        path: 'mapa',
        loadComponent: () =>
          import('@dominio/mapa/mapa-luminarias.component').then((m) => m.MapaLuminariasComponent),
        title: 'Mapa · FARO',
      },
      {
        path: 'eventos',
        loadComponent: () =>
          import('@dominio/eventos/eventos.component').then((m) => m.EventosComponent),
        title: 'Eventos · FARO',
        ...ve('eventos'),
      },

      // ── Inventario ─────────────────────────────────────────────────────────
      {
        path: 'inventario',
        children: [
          { path: '', redirectTo: 'luminarias', pathMatch: 'full' },
          {
            path: 'luminarias',
            loadComponent: () =>
              import('@dominio/inventario/luminarias/listado/listado-luminarias.component').then(
                (m) => m.ListadoLuminariasComponent,
              ),
            title: 'Luminarias · FARO',
          },
          {
            path: 'luminarias/:id',
            loadComponent: () =>
              import('@dominio/inventario/luminarias/detalle/detalle-luminaria.component').then(
                (m) => m.DetalleLuminariaComponent,
              ),
            title: 'Luminaria · FARO',
          },
          {
            path: 'puestas',
            loadComponent: () =>
              import('@dominio/inventario/puestas/listado-puestas.component').then(
                (m) => m.ListadoPuestasComponent,
              ),
            title: 'Puestas · FARO',
            ...ve('puestas'),
          },
          {
            path: 'puesta/:id',
            loadComponent: () =>
              import('@dominio/inventario/puestas/detalle-puesta.component').then(
                (m) => m.DetallePuestaComponent,
              ),
            title: 'Puesta · FARO',
            ...ve('puestas'),
          },
          {
            path: 'grupos/:categoria',
            loadComponent: () =>
              import('@dominio/inventario/grupos/listado-grupos.component').then(
                (m) => m.ListadoGruposComponent,
              ),
            title: 'Grupos · FARO',
            ...ve(pantallaGrupos),
          },
          {
            path: 'grupos/:categoria/:id',
            loadComponent: () =>
              import('@dominio/inventario/grupos/detalle-grupo.component').then(
                (m) => m.DetalleGrupoComponent,
              ),
            title: 'Grupo · FARO',
            ...ve(pantallaGrupos),
          },
          {
            // La ruta del "Asignar" de IRIX. En FARO asignar es un diálogo del
            // listado: se redirige a él con `?asignar=<id>` y el listado lo abre
            // (y chequea el permiso, que depende de la categoría).
            path: 'grupos/:categoria/:id/asignar',
            redirectTo: ({ params }) =>
              inject(Router).createUrlTree(['/inventario/grupos', params['categoria']], {
                queryParams: { asignar: params['id'] },
              }),
          },
          {
            path: 'zonas',
            loadComponent: () =>
              import('@dominio/inventario/zonas/listado-zonas.component').then(
                (m) => m.ListadoZonasComponent,
              ),
            title: 'Zonas · FARO',
          },
        ],
      },

      // ── Operación ──────────────────────────────────────────────────────────
      {
        path: 'operacion',
        children: [
          { path: '', redirectTo: 'comandos', pathMatch: 'full' },
          // Antes fue la jerarquía de perfiles (hoy un diálogo de los listados
          // de luminarias, grupos y puestas); desde el 07/10/2026 son los
          // perfiles de configuración, que estaban en Catálogos.
          {
            path: 'perfiles',
            loadComponent: () =>
              import('@dominio/operacion/perfiles/listado-perfiles.component').then(
                (m) => m.ListadoPerfilesComponent,
              ),
            title: 'Perfiles de configuración · FARO',
          },
          {
            path: 'comandos',
            loadComponent: () =>
              import('@dominio/operacion/comandos/comandos.component').then(
                (m) => m.ComandosComponent,
              ),
            title: 'Comandos enviados · FARO',
          },
          // Real contra deseada se ve por luminaria: modal de la Cronología y %
          // aplicado del listado. El link viejo cae en luminarias.
          { path: 'comparador', redirectTo: '/inventario/luminarias' },
          // Seguimiento: la solapa va en la ruta para que el "volver" de cada
          // detalle caiga en la suya.
          { path: 'seguimiento', redirectTo: 'seguimiento/perfiles', pathMatch: 'full' },
          {
            path: 'seguimiento/perfiles',
            loadComponent: () =>
              import('@dominio/operacion/seguimiento/seguimiento.component').then(
                (m) => m.SeguimientoComponent,
              ),
            data: { vista: 'perfiles' },
            title: 'Seguimiento de perfiles · FARO',
          },
          {
            path: 'seguimiento/perfiles/:id',
            loadComponent: () =>
              import('@dominio/operacion/seguimiento/perfiles/detalle-aplicacion.component').then(
                (m) => m.DetalleAplicacionComponent,
              ),
            title: 'Aplicación de perfil · FARO',
          },
          {
            path: 'seguimiento/encendidos',
            loadComponent: () =>
              import('@dominio/operacion/seguimiento/seguimiento.component').then(
                (m) => m.SeguimientoComponent,
              ),
            data: { vista: 'encendidos' },
            title: 'Seguimiento de encendidos · FARO',
          },
          {
            path: 'seguimiento/encendidos/:id',
            loadComponent: () =>
              import('@dominio/operacion/seguimiento/encendidos/detalle-encendido.component').then(
                (m) => m.DetalleEncendidoComponent,
              ),
            title: 'Encendido o apagado · FARO',
          },
          // Campañas pasó a Seguimiento: los links viejos caen en encendidos,
          // que es lo que quedó de ellas (los perfiles se siguen por acción).
          { path: 'campanas', redirectTo: 'seguimiento/encendidos' },
          { path: 'campanas/:id', redirectTo: 'seguimiento/encendidos/:id' },
        ],
      },

      // ── Red LoRaWAN ────────────────────────────────────────────────────────
      {
        path: 'red',
        children: [
          { path: '', redirectTo: 'nodos', pathMatch: 'full' },
          // En FARO los dispositivos LoRaWAN se llaman nodos; la ruta vieja
          // redirige para no romper links guardados.
          { path: 'dispositivos', redirectTo: 'nodos' },
          {
            path: 'nodos',
            loadComponent: () =>
              import('@dominio/red/nodos/listado-nodos.component').then(
                (m) => m.ListadoNodosComponent,
              ),
            title: 'Nodos · FARO',
            ...ve('nodos'),
          },
          {
            path: 'nodos/:id',
            loadComponent: () =>
              import('@dominio/red/nodos/detalle/detalle-nodo.component').then(
                (m) => m.DetalleNodoComponent,
              ),
            title: 'Nodo · FARO',
            ...ve('nodos'),
          },
          {
            path: 'gateways',
            loadComponent: () =>
              import('@dominio/red/gateways/listado-gateways.component').then(
                (m) => m.ListadoGatewaysComponent,
              ),
            title: 'Gateways · FARO',
            ...ve('gateways'),
          },
          {
            path: 'gateways/:id',
            loadComponent: () =>
              import('@dominio/red/gateways/detalle/detalle-gateway.component').then(
                (m) => m.DetalleGatewayComponent,
              ),
            ...ve('gateways'),
            title: 'Gateway · FARO',
          },
          {
            path: 'fila-ns',
            loadComponent: () =>
              import('@dominio/red/fila-ns/fila-ns.component').then((m) => m.FilaNsComponent),
            title: 'Fila del network server · FARO',
            // Sólo nivel 0: estado interno del NS y un botón que borra downlinks.
            ...ve('filaNs'),
          },
          {
            path: 'botones-ble',
            loadComponent: () =>
              import('@dominio/red/botones/listado-botones-ble.component').then(
                (m) => m.ListadoBotonesBleComponent,
              ),
            title: 'Botones BLE · FARO',
            ...ve('botonesBle'),
          },
          {
            path: 'botones-ble/:id',
            loadComponent: () =>
              import('@dominio/red/botones/detalle/detalle-boton-ble.component').then(
                (m) => m.DetalleBotonBleComponent,
              ),
            title: 'Botón BLE · FARO',
            ...ve('botonesBle'),
          },
          {
            path: 'metricas',
            loadComponent: () =>
              import('@dominio/red/metricas/metricas-downlinks.component').then(
                (m) => m.MetricasDownlinksComponent,
              ),
            title: 'Métricas de downlinks · FARO',
            ...ve('metricasDownlinks'),
          },
          {
            path: 'logs',
            loadComponent: () =>
              import('@dominio/red/logs/logs.component').then((m) => m.LogsComponent),
            title: 'Logs · FARO',
            ...ve('logs'),
          },
        ],
      },

      // ── Catálogos ──────────────────────────────────────────────────────────
      {
        path: 'catalogos',
        children: [
          { path: '', redirectTo: 'modelos-luminaria', pathMatch: 'full' },
          // Los dos catálogos de modelos comparten componente: es la misma
          // colección y `tipo` los separa. Ver `ListadoModelosComponent`.
          {
            path: 'modelos-luminaria',
            loadComponent: () =>
              import('@dominio/catalogos/modelos/listado-modelos.component').then(
                (m) => m.ListadoModelosComponent,
              ),
            data: { tipo: 'Luminaria' },
            title: 'Modelos de luminaria · FARO',
          },
          {
            path: 'modelos-dispositivo',
            loadComponent: () =>
              import('@dominio/catalogos/modelos/listado-modelos.component').then(
                (m) => m.ListadoModelosComponent,
              ),
            canActivate: [vistaGuard],
            data: { tipo: 'DispositivoLorawan', pantalla: 'modelosDispositivo' },
            title: 'Modelos de dispositivo · FARO',
          },
          {
            path: 'codigos',
            loadComponent: () =>
              import('@dominio/catalogos/codigos/listado-codigos.component').then(
                (m) => m.ListadoCodigosComponent,
              ),
            title: 'Códigos de dispositivo · FARO',
            ...ve('codigosDispositivo'),
          },
          // Los perfiles pasaron a Operación: los links viejos (favoritos,
          // correos) siguen andando, con su búsqueda y sus filtros.
          { path: 'perfiles', redirectTo: '/operacion/perfiles' },
        ],
      },

      // ── Mantenimiento (servicio técnico) ───────────────────────────────────
      {
        path: 'mantenimiento',
        children: [
          { path: '', redirectTo: 'solicitudes', pathMatch: 'full' },
          {
            path: 'solicitudes',
            loadComponent: () =>
              import('@dominio/mantenimiento/listado-solicitudes.component').then(
                (m) => m.ListadoSolicitudesComponent,
              ),
            ...ve('solicitudes'),
            title: 'Servicio técnico · FARO',
          },
          {
            path: 'materiales',
            loadComponent: () =>
              import('@dominio/mantenimiento/materiales.component').then(
                (m) => m.MaterialesComponent,
              ),
            ...ve('materiales'),
            title: 'Materiales · FARO',
          },
          {
            path: 'tecnicos',
            loadComponent: () =>
              import('@dominio/mantenimiento/listado-tecnicos.component').then(
                (m) => m.ListadoTecnicosComponent,
              ),
            ...ve('tecnicos'),
            title: 'Técnicos · FARO',
          },
        ],
      },

      // ── Administración ─────────────────────────────────────────────────────
      // Las tres pantallas están protegidas de verdad: el permiso de ver no
      // alcanza para entrar por link pegado a mano si no está la acción.
      {
        path: 'administracion',
        children: [
          { path: '', redirectTo: 'clientes', pathMatch: 'full' },
          {
            path: 'clientes',
            loadComponent: () =>
              import('@dominio/administracion/clientes/listado-clientes.component').then(
                (m) => m.ListadoClientesComponent,
              ),
            ...ve('clientes'),
            title: 'Clientes · FARO',
          },
          {
            path: 'clientes/:id',
            loadComponent: () =>
              import('@dominio/administracion/clientes/detalle-cliente.component').then(
                (m) => m.DetalleClienteComponent,
              ),
            ...ve('clientes'),
            title: 'Cliente · FARO',
          },
          {
            path: 'usuarios',
            loadComponent: () =>
              import('@dominio/administracion/usuarios/listado-permisos.component').then(
                (m) => m.ListadoPermisosComponent,
              ),
            ...ve('usuarios'),
            title: 'Usuarios y permisos · FARO',
          },
          {
            path: 'roles',
            loadComponent: () =>
              import('@dominio/administracion/roles/listado-roles.component').then(
                (m) => m.ListadoRolesComponent,
              ),
            ...ve('roles'),
            title: 'Roles · FARO',
          },
        ],
      },

      // ── Configuración ──────────────────────────────────────────────────────
      // El cliente y el usuario con los que se entró, como en IRIX.
      {
        path: 'configuracion',
        children: [
          { path: '', redirectTo: 'usuario', pathMatch: 'full' },
          {
            // El detalle de cliente de Administración, en modo «propio»: el id
            // sale de la sesión y no de la URL (`data.propio` entra como input).
            // Se referencia desde acá porque un dominio no puede importar de
            // otro; las rutas sí conocen a todos.
            path: 'cliente',
            loadComponent: () =>
              import('@dominio/administracion/clientes/detalle-cliente.component').then(
                (m) => m.DetalleClienteComponent,
              ),
            canActivate: [vistaGuard],
            data: { pantalla: 'configurarCliente', propio: true },
            title: 'Configurar cliente · FARO',
          },
          {
            path: 'usuario',
            loadComponent: () =>
              import('@dominio/configuracion/usuario/configurar-usuario.component').then(
                (m) => m.ConfigurarUsuarioComponent,
              ),
            ...ve('configurarUsuario'),
            title: 'Configurar usuario · FARO',
          },
        ],
      },

      // ── Ayuda ──────────────────────────────────────────────────────────────
      // Sin `ve(...)`: la ve cualquiera. Lo que muestra se filtra por permisos
      // adentro, artículo por artículo y sección por sección.
      {
        path: 'ayuda',
        loadComponent: () => import('@dominio/ayuda/ayuda.component').then((m) => m.AyudaComponent),
        title: 'Ayuda · FARO',
      },
      // Qué cambió en cada versión. Sin permiso propio: es para todos.
      {
        path: 'novedades',
        loadComponent: () =>
          import('@dominio/novedades/novedades.component').then((m) => m.NovedadesComponent),
        title: 'Novedades · FARO',
      },

      // ── Informes ───────────────────────────────────────────────────────────
      // La solapa se sacó el 24/09/2026: lo que tenía de propio pasó a fichas
      // del panorama. Los links viejos (guardados, mandados por correo) caen ahí.
      { path: 'informes', children: [{ path: '**', redirectTo: '/panorama' }] },

      {
        path: 'sin-permiso',
        loadComponent: () =>
          import('@core/permisos/sin-permiso.component').then((m) => m.SinPermisoComponent),
        title: 'Sin permiso · FARO',
      },
    ],
  },

  { path: '**', redirectTo: '' },
];
